import React, { useState, useEffect } from 'react';
import { clockAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import {
  Clock,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  LogIn,
  LogOut,
  Briefcase,
  Phone,
  AlertCircle,
} from 'lucide-react';

const SiteWorkerClock = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [alert, setAlert] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState('');

  useEffect(() => {
    loadStatus();
    getLocation();
  }, []);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          setLocationError(null);
        },
        (err) => {
          setLocationError('Unable to get your location. Please enable location services.');
          console.error('Geolocation error:', err);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
    }
  };

  const loadStatus = async () => {
    try {
      const res = await clockAPI.getSelfStatus();
      setStatus(res.data);
      if (res.data.assigned_jobs_today?.length > 0) {
        setSelectedJobId(res.data.assigned_jobs_today[0].id);
      }
    } catch (error) {
      toast.error('Failed to load clock status');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    if (!location) {
      toast.error('Please enable location services to clock in');
      return;
    }

    try {
      setClockingIn(true);
      const res = await clockAPI.selfClockIn({
        latitude: location.latitude,
        longitude: location.longitude,
        job_id: selectedJobId || null,
      });

      // Check for alerts
      if (res.data.is_unpaid || res.data.outside_geofence) {
        setAlert({
          type: res.data.is_unpaid ? 'unpaid' : 'outside_geofence',
          message: res.data.message,
          requires_supervisor_contact: res.data.requires_supervisor_contact,
        });
      } else {
        toast.success('Clocked in successfully');
      }

      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setClockingOut(true);
      await clockAPI.selfClockOut({
        latitude: location?.latitude,
        longitude: location?.longitude,
      });
      toast.success('Clocked out successfully');
      setAlert(null);
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const isClockdIn = status?.clocked_in;
  const currentEntry = status?.current_entry;
  const assignedJobs = status?.assigned_jobs_today || [];
  const hasAssignedJob = status?.has_assigned_job;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 p-4">
      <div className="max-w-md mx-auto pt-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png" 
            alt="MBConstruction" 
            className="w-20 h-20 mx-auto mb-4 rounded-full"
          />
          <h1 className="text-2xl font-bold text-orange-500 mb-1">Time Clock</h1>
          <p className="text-slate-400">Welcome, {user?.name}</p>
        </div>

        {/* Alert Message */}
        {alert && (
          <div className={`mb-6 p-4 rounded-xl ${alert.type === 'unpaid' ? 'bg-red-900/50 border border-red-500' : 'bg-amber-900/50 border border-amber-500'}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-6 h-6 flex-shrink-0 ${alert.type === 'unpaid' ? 'text-red-400' : 'text-amber-400'}`} />
              <div>
                <p className={`text-sm ${alert.type === 'unpaid' ? 'text-red-200' : 'text-amber-200'}`}>
                  {alert.message}
                </p>
                {alert.requires_supervisor_contact && (
                  <div className="mt-3 flex items-center gap-2 text-red-300">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">Contact your supervisor immediately</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setAlert(null)}
              className="mt-3 text-sm text-slate-400 hover:text-white"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          {/* Current Status */}
          <div className="text-center mb-6">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${isClockdIn ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
              {isClockdIn ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Clocked In</span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Not Clocked In</span>
                </>
              )}
            </div>
          </div>

          {/* Current Entry Info */}
          {isClockdIn && currentEntry && (
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <h3 className="text-sm font-medium text-slate-500 mb-2">Current Session</h3>
              <div className="space-y-2">
                {currentEntry.job_title && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Briefcase className="w-4 h-4" />
                    <span>{currentEntry.job_code} - {currentEntry.job_title}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-700">
                  <Clock className="w-4 h-4" />
                  <span>Started: {currentEntry.clock_in_time && format(new Date(currentEntry.clock_in_time), 'HH:mm')}</span>
                </div>
                {currentEntry.is_unpaid && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">UNPAID - No job assigned</span>
                  </div>
                )}
                {currentEntry.clock_in_outside_geofence && (
                  <div className="flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Outside geofence ({currentEntry.clock_in_distance_from_site}m away)</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Job Selection (when not clocked in) */}
          {!isClockdIn && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Job
              </label>
              {assignedJobs.length > 0 ? (
                <select
                  value={selectedJobId}
                  onChange={(e) => setSelectedJobId(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-slate-700"
                >
                  {assignedJobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.job_code} - {job.title}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-800 font-medium">No Jobs Assigned</p>
                      <p className="text-amber-700 text-sm mt-1">
                        You don't have any jobs assigned for today. If you clock in, your time will be marked as unpaid.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Location Status */}
          <div className="flex items-center gap-2 text-sm mb-6">
            <MapPin className={`w-4 h-4 ${location ? 'text-green-500' : 'text-red-500'}`} />
            {location ? (
              <span className="text-slate-600">Location acquired</span>
            ) : (
              <span className="text-red-600">{locationError || 'Acquiring location...'}</span>
            )}
            <button
              onClick={getLocation}
              className="ml-auto text-blue-600 hover:text-blue-700 text-sm"
            >
              Refresh
            </button>
          </div>

          {/* Clock Buttons */}
          {!isClockdIn ? (
            <button
              onClick={handleClockIn}
              disabled={clockingIn || !location}
              className="w-full py-4 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {clockingIn ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Clocking In...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Clock In
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleClockOut}
              disabled={clockingOut}
              className="w-full py-4 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {clockingOut ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Clocking Out...
                </>
              ) : (
                <>
                  <LogOut className="w-5 h-5" />
                  Clock Out
                </>
              )}
            </button>
          )}
        </div>

        {/* Today's Assigned Jobs */}
        {assignedJobs.length > 0 && (
          <div className="bg-white/10 rounded-xl p-4">
            <h3 className="text-sm font-medium text-slate-300 mb-3">Today's Assigned Jobs</h3>
            <div className="space-y-2">
              {assignedJobs.map((job) => (
                <div key={job.id} className="bg-white/10 rounded-lg p-3">
                  <p className="text-white font-medium">{job.job_code}</p>
                  <p className="text-slate-300 text-sm">{job.title}</p>
                  {job.location && (
                    <p className="text-slate-400 text-xs flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {job.location}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date/Time Display */}
        <div className="text-center mt-8 text-slate-400">
          <p className="text-2xl font-mono">{format(new Date(), 'HH:mm:ss')}</p>
          <p className="text-sm mt-1">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
        </div>
      </div>
    </div>
  );
};

export default SiteWorkerClock;
